import React, { useEffect, useState, useCallback, useRef } from "react";
import api from "../../api";
import { toast } from "sonner";
import { Stamp, Upload, Trash2, Image as ImageIcon, Save, Eye, ArrowUpToLine, ArrowDownToLine } from "lucide-react";

const COMPANIES = [
  { code: "teleborsa", label: "Teleborsa srl" },
  { code: "sit", label: "S.I.T. srl" },
];

const MARGIN_BOUNDS = { topMin: 10, topMax: 140, bottomMin: 5, bottomMax: 120 };

const CompanyLetterhead = ({ code, label, conf, onChanged }) => {
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [mTop, setMTop] = useState(62);
  const [mBottom, setMBottom] = useState(40);
  const [savingMargins, setSavingMargins] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (conf) {
      if (typeof conf.margin_top === "number") setMTop(conf.margin_top);
      if (typeof conf.margin_bottom === "number") setMBottom(conf.margin_bottom);
    }
  }, [conf?.margin_top, conf?.margin_bottom]);

  useEffect(() => {
    let url;
    if (conf?.file_id) {
      api.get(`/files/${conf.file_id}`, { responseType: "blob" })
        .then((r) => { url = URL.createObjectURL(r.data); setPreview(url); })
        .catch(() => setPreview(null));
    } else {
      setPreview(null);
    }
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [conf?.file_id]);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!["image/png", "image/jpeg", "image/jpg"].includes(f.type)) { toast.error("Usa un'immagine PNG o JPG"); return; }
    if (f.size > 5 * 1024 * 1024) { toast.error("Immagine troppo grande (max 5 MB)"); return; }
    const fd = new FormData();
    fd.append("company", code);
    fd.append("file", f);
    setUploading(true);
    try {
      await api.post("/settings/letterhead", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Carta intestata aggiornata per ${label}`);
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore nel caricamento");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/settings/letterhead/${code}`);
      toast.success(`Carta intestata rimossa per ${label}`);
      onChanged();
    } catch { toast.error("Errore durante la rimozione"); }
  };

  const clamp = (v, min, max) => Math.min(Math.max(Number(v) || 0, min), max);

  const saveMargins = async () => {
    const top = clamp(mTop, MARGIN_BOUNDS.topMin, MARGIN_BOUNDS.topMax);
    const bottom = clamp(mBottom, MARGIN_BOUNDS.bottomMin, MARGIN_BOUNDS.bottomMax);
    setMTop(top); setMBottom(bottom);
    setSavingMargins(true);
    try {
      await api.put(`/settings/letterhead/${code}/margins`, { margin_top: top, margin_bottom: bottom });
      toast.success(`Margini salvati per ${label}`);
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore nel salvataggio dei margini");
    } finally {
      setSavingMargins(false);
    }
  };

  const openPreview = async () => {
    setPreviewing(true);
    try {
      const res = await api.get(`/settings/letterhead/${code}/preview`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore nella generazione dell'anteprima");
    } finally {
      setPreviewing(false);
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-5" data-testid={`letterhead-${code}`}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-[15px] font-bold text-[#16345c]">{label}</h3>
        {conf?.file_id && <span className="text-[11px] text-gray-400">Agg. {conf.updated_at ? new Date(conf.updated_at).toLocaleDateString("it-IT") : ""}</span>}
      </div>
      {preview ? (
        <img src={preview} alt={`Carta intestata ${label}`} data-testid={`letterhead-preview-${code}`}
          className="w-full max-h-40 object-contain border border-gray-100 rounded bg-gray-50 mb-3" />
      ) : (
        <div className="w-full h-28 flex flex-col items-center justify-center text-gray-300 border border-dashed border-gray-300 rounded mb-3">
          <ImageIcon size={28} /> <span className="text-[12px] mt-1">Nessuna carta intestata</span>
        </div>
      )}
      <div className="flex items-center gap-2">
        <label className="flex-1 flex items-center justify-center gap-2 cursor-pointer bg-[#16345c] hover:bg-[#0d1b2f] text-white text-[13px] font-bold px-3 py-2 rounded-sm transition-colors">
          <Upload size={15} /> {uploading ? "Caricamento..." : (conf?.file_id ? "Sostituisci" : "Carica PNG/JPG")}
          <input ref={fileRef} type="file" accept="image/png,image/jpeg" className="hidden" data-testid={`letterhead-input-${code}`} onChange={onFile} disabled={uploading} />
        </label>
        {conf?.file_id && (
          <button onClick={remove} data-testid={`letterhead-remove-${code}`}
            className="flex items-center gap-1 text-[13px] font-semibold text-[#c00510] border border-red-300 hover:bg-red-50 px-3 py-2 rounded-sm transition-colors">
            <Trash2 size={15} /> Rimuovi
          </button>
        )}
      </div>

      {/* Margini regolabili del contenuto dell'attestato */}
      <div className="mt-4 pt-4 border-t border-gray-100">
        <p className="text-[12px] font-bold text-[#16345c] mb-2">Margini contenuto (mm)</p>
        <p className="text-[11px] text-gray-500 mb-3">Regola lo spazio in alto (sotto l'intestazione) e in basso (sopra il piè di pagina) così il testo si adatta alla carta intestata.</p>
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="flex items-center gap-1 text-[11px] font-semibold text-gray-600 mb-1"><ArrowUpToLine size={13} /> Superiore</span>
            <input
              type="number" min={MARGIN_BOUNDS.topMin} max={MARGIN_BOUNDS.topMax}
              value={mTop} onChange={(e) => setMTop(e.target.value)}
              data-testid={`letterhead-margin-top-${code}`}
              className="w-full border border-gray-200 rounded-sm px-2.5 py-1.5 text-[13px] focus:border-[#16345c] outline-none"
            />
          </label>
          <label className="block">
            <span className="flex items-center gap-1 text-[11px] font-semibold text-gray-600 mb-1"><ArrowDownToLine size={13} /> Inferiore</span>
            <input
              type="number" min={MARGIN_BOUNDS.bottomMin} max={MARGIN_BOUNDS.bottomMax}
              value={mBottom} onChange={(e) => setMBottom(e.target.value)}
              data-testid={`letterhead-margin-bottom-${code}`}
              className="w-full border border-gray-200 rounded-sm px-2.5 py-1.5 text-[13px] focus:border-[#16345c] outline-none"
            />
          </label>
        </div>
        <div className="flex items-center gap-2 mt-3">
          <button onClick={saveMargins} disabled={savingMargins} data-testid={`letterhead-save-margins-${code}`}
            className="flex items-center gap-1.5 bg-[#16345c] hover:bg-[#0d1b2f] disabled:opacity-60 text-white text-[13px] font-bold px-3 py-2 rounded-sm transition-colors">
            <Save size={15} /> {savingMargins ? "Salvataggio..." : "Salva margini"}
          </button>
          <button onClick={openPreview} disabled={previewing} data-testid={`letterhead-preview-btn-${code}`}
            className="flex items-center gap-1.5 border border-[#16345c] text-[#16345c] hover:bg-[#16345c] hover:text-white disabled:opacity-60 text-[13px] font-bold px-3 py-2 rounded-sm transition-colors">
            <Eye size={15} /> {previewing ? "Genero..." : "Anteprima"}
          </button>
        </div>
      </div>
    </div>
  );
};

export const LetterheadTab = () => {
  const [conf, setConf] = useState({});
  const load = useCallback(() => api.get("/settings/letterhead").then((r) => setConf(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="letterhead-tab">
      <div className="mb-4">
        <h2 className="text-[16px] font-bold text-[#16345c] flex items-center gap-2"><Stamp size={18} /> Carta intestata degli attestati</h2>
        <p className="text-[13px] text-gray-500 mt-1">Carica un'immagine (PNG o JPG) usata come intestazione negli attestati di servizio generati dai dipendenti. Con un foglio A4 completo (intestazione in alto e piè di pagina in basso) usa i margini per posizionare il testo. Puoi impostare tutto in modo diverso per ciascuna società.</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {COMPANIES.map((c) => <CompanyLetterhead key={c.code} code={c.code} label={c.label} conf={conf[c.code]} onChanged={load} />)}
      </div>
    </div>
  );
};

export default LetterheadTab;
